import type { UmgebungSummary } from '@etown/shared';
import { navigate } from '../../hooks/useHashRoute';
import { leseLetzteUmgebung } from '../../lib/letzteUmgebung';
import { officeRoute, SUPERADMIN_ROUTE } from '../../lib/routen';
import type { SessionUser } from '../../net/api';
import { useSessionStore } from '../../stores/sessionStore';
import './umgebung.css';

const ANSICHT_TEXT = { '2d': 'Draufsicht', '3d': 'Ego-Perspektive', beide: 'Draufsicht & Ego' } as const;

function rollenText(u: UmgebungSummary, isSuperadmin: boolean): string {
  if (isSuperadmin) return 'Superadmin';
  return u.rolle === 'admin' ? 'Admin' : 'Mitglied';
}

/**
 * Wohin? Die zuletzt besuchte Umgebung steht gross voran, die uebrigen
 * folgen als Liste. Ohne eine einzige Umgebung gibt es nur den Hinweis.
 */
export function UmgebungsAuswahl({ user }: { user: SessionUser }) {
  const logout = useSessionStore((s) => s.logout);
  const letzteId = leseLetzteUmgebung();
  const letzte = user.umgebungen.find((u) => u.id === letzteId) ?? user.umgebungen[0];
  const weitere = user.umgebungen.filter((u) => u.id !== letzte?.id);

  return (
    <main className="auswahl">
      <section className="auswahl__panel">
        <header className="auswahl__kopf">
          <span className="auswahl__dot" aria-hidden="true" />
          <div>
            <h1 className="auswahl__titel">Wohin, {user.displayName}?</h1>
            <p className="auswahl__untertitel">Deine Umgebungen</p>
          </div>
        </header>

        {!letzte && (
          <p className="auswahl__leer">
            Du bist noch keiner Umgebung zugeordnet. Frag eine Administratorin nach Zugang.
          </p>
        )}

        {letzte && (
          <button className="auswahl__haupt" onClick={() => navigate(officeRoute(letzte.id))}>
            <span className="auswahl__haupt-label">{letzte.id === letzteId ? 'Zuletzt besucht' : 'Empfohlen'}</span>
            <span className="auswahl__haupt-name">{letzte.name}</span>
            <span className="auswahl__meta">
              <span className="auswahl__badge">{rollenText(letzte, user.isSuperadmin)}</span>
              <span>{ANSICHT_TEXT[letzte.ansichten]}</span>
            </span>
            <span className="auswahl__haupt-cta">Betreten →</span>
          </button>
        )}

        {weitere.length > 0 && (
          <ul className="auswahl__liste">
            {weitere.map((u) => (
              <li key={u.id}>
                <button className="auswahl__eintrag" onClick={() => navigate(officeRoute(u.id))}>
                  <span className="auswahl__eintrag-name">{u.name}</span>
                  <span className="auswahl__meta">
                    <span className="auswahl__badge">{rollenText(u, user.isSuperadmin)}</span>
                    <span>{ANSICHT_TEXT[u.ansichten]}</span>
                  </span>
                  <span className="auswahl__pfeil" aria-hidden="true">→</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <footer className="auswahl__fuss">
          {user.isSuperadmin && (
            <button className="auswahl__link" onClick={() => navigate(SUPERADMIN_ROUTE)}>
              Superadmin
            </button>
          )}
          <button className="auswahl__link" onClick={() => void logout()}>
            Abmelden
          </button>
        </footer>
      </section>
    </main>
  );
}
